<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowDown,
  Bell,
  DataAnalysis,
  DocumentCopy,
  Fold,
  Lightning,
  Menu as MenuIcon,
  Odometer,
  Setting,
  Tools,
  Umbrella,
  Van,
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'

import { apiRequest } from '@/api/client'
import type { InsuranceReminderItem, InsuranceReminderResponse } from '@/api/types'
import IcpFilingLink from '@/components/IcpFilingLink.vue'
import { INSURANCE_REMINDERS_CHANGED_EVENT } from '@/events/insuranceReminders'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const mobileMenuOpen = ref(false)
const reminderDrawerOpen = ref(false)
const reminderPopupOpen = ref(false)
const reminderLoading = ref(false)
const acknowledgmentLoading = ref(false)
const reminder = ref<InsuranceReminderResponse>({
  asOfDate: '', expiringVehicleCount: 0, expiredVehicleCount: 0,
  items: [], popupPolicyIds: [], shouldPopup: false,
})

const menuItems = [
  { path: '/dashboard', label: '数据看板', icon: DataAnalysis, permission: 'dashboard:read' },
  { path: '/vehicles', label: '车辆档案', icon: Van, permission: 'vehicle:read' },
  { path: '/energy', label: '补能管理', icon: Lightning, permission: 'energy:read' },
  { path: '/maintenance', label: '维修保养', icon: Tools, permission: 'maintenance:read' },
  { path: '/insurance', label: '保险管理', icon: Umbrella, permission: 'insurance:read' },
  { path: '/data-exchange', label: '数据导入导出', icon: DocumentCopy, permission: 'data:import' },
  { path: '/system', label: '系统管理', icon: Setting, permission: 'user:manage' },
]

const visibleMenuItems = computed(() => menuItems.filter((item) => auth.can(item.permission)))
const pageTitle = computed(() => String(route.meta.title ?? '车辆管理'))
const initials = computed(() => auth.user?.fullName.slice(-2) ?? '用户')
const canViewInsuranceReminder = computed(() => auth.can('insurance:reminder'))
const popupItems = computed(() => {
  const ids = new Set(reminder.value.popupPolicyIds)
  return reminder.value.items.filter((item) => ids.has(item.policyId))
})

onMounted(() => {
  window.addEventListener(INSURANCE_REMINDERS_CHANGED_EVENT, handleRemindersChanged)
  if (canViewInsuranceReminder.value) void loadReminders(true)
})

onBeforeUnmount(() => {
  window.removeEventListener(INSURANCE_REMINDERS_CHANGED_EVENT, handleRemindersChanged)
})

function handleRemindersChanged(): void {
  void loadReminders(false)
}

async function handleUserCommand(command: string): Promise<void> {
  if (command === 'change-password') {
    await router.push('/change-password')
    return
  }
  if (command !== 'logout') return
  await auth.logout()
  ElMessage.success('已安全退出')
  await router.replace('/login')
}

function closeMobileMenu(): void {
  mobileMenuOpen.value = false
}

async function loadReminders(showPopup: boolean): Promise<void> {
  if (!canViewInsuranceReminder.value) return
  reminderLoading.value = true
  try {
    reminder.value = await apiRequest<InsuranceReminderResponse>('/reminders/insurance')
    if (showPopup && reminder.value.shouldPopup) reminderPopupOpen.value = true
  } catch (error) {
    if (showPopup) ElMessage.error(error instanceof Error ? error.message : '保险到期提醒加载失败')
  } finally {
    reminderLoading.value = false
  }
}

async function openReminderDrawer(): Promise<void> {
  if (!canViewInsuranceReminder.value) return
  reminderDrawerOpen.value = true
  await loadReminders(false)
}

async function acknowledge(type: 'DISMISS_TODAY' | 'SUPPRESS_POLICY', policyIds: number[]): Promise<void> {
  if (!policyIds.length) {
    reminderPopupOpen.value = false
    return
  }
  acknowledgmentLoading.value = true
  try {
    await apiRequest<void>('/reminders/insurance/acknowledgments', {
      method: 'POST', body: JSON.stringify({ policyIds, acknowledgmentType: type }),
    })
    reminderPopupOpen.value = false
    ElMessage.success(type === 'SUPPRESS_POLICY' ? '本批保单将不再主动弹窗提醒' : '今日已关闭本批到期提醒')
    await loadReminders(false)
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '提醒确认失败')
  } finally {
    acknowledgmentLoading.value = false
  }
}

async function openPolicy(item: InsuranceReminderItem): Promise<void> {
  reminderDrawerOpen.value = false
  reminderPopupOpen.value = false
  await router.push(`/insurance/${item.policyId}`)
}
</script>

<template>
  <div class="app-shell">
    <aside class="app-sidebar desktop-sidebar">
      <div class="brand-block">
        <span class="brand-mark"><Van /></span>
        <span>
          <strong>徐州港华</strong>
          <small>车辆管理系统</small>
        </span>
      </div>
      <el-menu :default-active="route.path" router class="side-menu">
        <el-menu-item v-for="item in visibleMenuItems" :key="item.path" :index="item.path">
          <el-icon><component :is="item.icon" /></el-icon>
          <span>{{ item.label }}</span>
        </el-menu-item>
      </el-menu>
      <div class="sidebar-foot">
        <Odometer />
        <span>车辆全生命周期台账 · v0.8</span>
      </div>
    </aside>

    <el-drawer
      v-model="mobileMenuOpen"
      direction="ltr"
      size="280px"
      :show-close="false"
      class="mobile-nav-drawer"
    >
      <template #header>
        <div class="brand-block drawer-brand">
          <span class="brand-mark"><Van /></span>
          <span><strong>徐州港华</strong><small>车辆管理系统</small></span>
        </div>
      </template>
      <el-menu :default-active="route.path" router class="side-menu" @select="closeMobileMenu">
        <el-menu-item v-for="item in visibleMenuItems" :key="item.path" :index="item.path">
          <el-icon><component :is="item.icon" /></el-icon>
          <span>{{ item.label }}</span>
        </el-menu-item>
      </el-menu>
    </el-drawer>

    <div class="app-workspace">
      <header class="app-header">
        <button class="mobile-menu-button" type="button" aria-label="打开导航" @click="mobileMenuOpen = true">
          <MenuIcon />
        </button>
        <div class="page-heading">
          <span>企业车辆管理</span>
          <h1 v-if="!route.meta.hideHeaderTitle">{{ pageTitle }}</h1>
        </div>
        <div class="header-actions">
          <el-tooltip v-if="canViewInsuranceReminder" content="查看保险到期提醒" placement="bottom">
            <el-badge :value="reminder.items.length" :hidden="!reminder.items.length" :max="99" class="reminder-badge">
              <button class="icon-button" type="button" aria-label="保险到期提醒" @click="openReminderDrawer">
                <Bell />
              </button>
            </el-badge>
          </el-tooltip>
          <span class="header-divider" />
          <el-dropdown trigger="click" @command="handleUserCommand">
            <button class="user-button" type="button">
              <span class="user-avatar">{{ initials }}</span>
              <span class="user-copy">
                <strong>{{ auth.user?.fullName }}</strong>
                <small>{{ auth.user?.roles.includes('ADMIN') ? '系统管理员' : auth.user?.roles.includes('BUSINESS_STAFF') ? '车辆业务人员' : '普通用户' }}</small>
              </span>
              <ArrowDown class="dropdown-arrow" />
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="change-password">修改密码</el-dropdown-item>
                <el-dropdown-item command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </header>

      <main class="app-main">
        <router-view />
      </main>
      <footer class="app-footer"><IcpFilingLink /></footer>
    </div>

    <el-drawer v-if="canViewInsuranceReminder" v-model="reminderDrawerOpen" title="保险到期提醒" size="min(440px, 100%)" class="reminder-drawer">
      <div class="reminder-summary"><span><strong>{{ reminder.expiringVehicleCount }}</strong> 辆 30 天内到期</span><span><strong>{{ reminder.expiredVehicleCount }}</strong> 辆已到期未续保</span></div>
      <div v-loading="reminderLoading" class="reminder-list">
        <button v-for="item in reminder.items" :key="item.policyId" type="button" class="reminder-item" @click="openPolicy(item)">
          <span class="reminder-status-dot" :class="item.policyStatus.toLowerCase()" />
          <span class="reminder-item-copy"><strong>{{ item.plateNo }}</strong><small>{{ item.insurerName || '未选择保险公司' }} · {{ item.coverageNames.join('、') }}</small><em>{{ item.expiryDate }} 到期 · {{ item.daysUntilExpiry >= 0 ? `剩余 ${item.daysUntilExpiry} 天` : `已过期 ${Math.abs(item.daysUntilExpiry)} 天` }}</em></span>
          <el-tag :type="item.policyStatus === 'EXPIRED' ? 'danger' : 'warning'" size="small">{{ item.policyStatus === 'EXPIRED' ? '已到期' : '即将到期' }}</el-tag>
        </button>
        <el-empty v-if="!reminderLoading && !reminder.items.length" :image-size="72" description="暂无到期或已过期未续保车辆" />
      </div>
      <template #footer><el-button @click="router.push('/insurance'); reminderDrawerOpen = false">进入保险台账</el-button></template>
    </el-drawer>

    <el-dialog v-if="canViewInsuranceReminder" v-model="reminderPopupOpen" title="保险即将到期" width="560px" class="reminder-dialog" :show-close="false" :close-on-click-modal="false" :close-on-press-escape="false">
      <el-alert type="warning" :closable="false" show-icon title="以下车辆保险将在 30 天内到期，请及时安排续保。" />
      <div class="reminder-popup-list">
        <button v-for="item in popupItems" :key="item.policyId" type="button" class="reminder-popup-item" @click="openPolicy(item)"><span><strong>{{ item.plateNo }}</strong><small>{{ item.coverageNames.join('、') }}</small></span><em>{{ item.expiryDate }} · 剩余 {{ item.daysUntilExpiry }} 天</em></button>
      </div>
      <template #footer><el-button :loading="acknowledgmentLoading" @click="acknowledge('DISMISS_TODAY', reminder.popupPolicyIds)">今日关闭</el-button><el-button type="primary" :loading="acknowledgmentLoading" @click="acknowledge('SUPPRESS_POLICY', reminder.popupPolicyIds)">不再提醒本批次</el-button></template>
    </el-dialog>
  </div>
</template>
